/**
 * Mission queue panel — SA-270.
 *
 * Shows the mission's current queue-lane position (state / "since" / waiting
 * on) and assignee, supports a manual queue-state transition with a required
 * reason, and renders the read-only time-measures summary + event timeline
 * (spec §6). Self-fetches events/measures/assignees on mount; renders
 * nothing for non-quote orders (parent guards `order.orderType === 'quote'`,
 * same contract as FeasibilityPanel). Data methods (updateQueueState/
 * assignOrder/getOrderEvents/getOrderMeasures/getAssignees) come from
 * useOrders as props — the panel owns no fetch plumbing of its own beyond
 * orchestrating those calls, only its form/UI state, mirroring
 * FeasibilityPanel's contract with OrdersPage/InboxOrderSidebar.
 */

import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { cn } from '@/utils'
import { formatRelativeTime } from '@/utils/relativeTime'
import { QUEUE_STATE_CONFIG, WAITING_ON_LABELS } from '@/constants/queueStates'
import { GATE_STATE_CONFIG, STAFF_SETTABLE_GATE_STATES } from '@/constants/gateStates'
import { describeProcessEvent } from '@/components/process/processEventText'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { humanizeEventType, titleizeSlug } from '@/components/records/eventLabels'
import { RecordSection } from '@/components/records/RecordSection'
import { firstQuoteState } from '@/components/records/firstQuote'
import { durationWords, MEASURE_LABEL, NO_QUOTE_YET } from '@/components/records/firstQuoteText'
import { queueSummary } from '@/components/records/sectionSummaries'
import type {
  Order, QueueState, WaitingOn, OrderEventItem, OrderMeasures, SimilarMissionsMarker,
  GateState, OrderGateRow, OrderGateCommitment, OrderGatesPayload,
} from '@/types/commerce'

// SA-271 spec §0.4 controlled quick-pick list for the duplicate-resolution
// reason input — softness matches the revision_reason slot's controlled
// examples (which already name 'duplicate' and "couldn't find previous
// result").
const DUPLICATE_REASON_QUICK_PICKS = [
  'changed payload', 'changed date', 'changed aircraft', 'customer revision',
  'revalidation', 'new restriction', "couldn't find previous result", 'duplicate',
]

const QUEUE_STATE_KEYS = Object.keys(QUEUE_STATE_CONFIG) as QueueState[]
const WAITING_ON_KEYS = Object.keys(WAITING_ON_LABELS) as WaitingOn[]

interface AssigneeOption {
  id: string
  name: string
}

interface QueuePanelProps {
  order: Order
  onChanged: () => void
  updateQueueState: (
    orderId: string, p: { queueState: QueueState; waitingOn?: WaitingOn; reason: string },
  ) => Promise<Order>
  assignOrder: (orderId: string, userId: string | null) => Promise<Order>
  getOrderEvents: (orderId: string, limit?: number) => Promise<OrderEventItem[]>
  getOrderMeasures: (orderId: string) => Promise<OrderMeasures>
  getAssignees: () => Promise<AssigneeOption[]>
  resolveDuplicate: (
    orderId: string, p: { action: 'proceed_new' | 'reuse_delta'; matchedOrderId?: string; reason: string },
  ) => Promise<Order>
  // SA-284 department gates
  getOrderGates: (orderId: string) => Promise<OrderGatesPayload>
  updateGateState: (
    orderId: string, department: string, p: { state: GateState; reason?: string },
  ) => Promise<OrderGatesPayload>
  /** `cards` (default): today's boxes, byte for byte — the form frame. `section`:
   * the record lane's one Queue section (SA-461, spec §7.4) without the ledger
   * (the Activity section reads it) and with the page's assignee map. */
  chrome?: 'cards' | 'section'
  /** id → display name from the page (SA-330): given, the panel never calls `getAssignees`. */
  assigneeNames?: Record<string, string>
  /** `?panel=queue` (section chrome only). */
  focused?: boolean
}

// `state: ''` is the "no explicit choice made yet" sentinel — a row's
// current state (which may be `auto_checked`, never staff-settable) must
// never be silently resubmitted as a real transition just because staff
// clicked Update without touching the dropdown.
interface GateForm {
  state: GateState | ''
  reason: string
}

const EMPTY_GATE_FORM: GateForm = { state: '', reason: '' }

export function QueuePanel({
  order, onChanged, updateQueueState, assignOrder, getOrderEvents, getOrderMeasures, getAssignees,
  resolveDuplicate, getOrderGates, updateGateState,
  chrome = 'cards', assigneeNames, focused = false,
}: QueuePanelProps) {
  const noun = useRecordNoun().singular
  const [events, setEvents] = useState<OrderEventItem[]>([])
  const [measures, setMeasures] = useState<OrderMeasures | null>(null)
  const [assignees, setAssignees] = useState<AssigneeOption[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // SA-284: department gates roster + §0.9 consolidated commitment.
  const [gates, setGates] = useState<OrderGateRow[]>([])
  const [commitment, setCommitment] = useState<OrderGateCommitment | null>(null)
  const [gateForms, setGateForms] = useState<Record<string, GateForm>>({})
  const [gateSubmitting, setGateSubmitting] = useState<string | null>(null)

  // SA-287: '' is the placeholder sentinel — an unqueued mission starts on
  // "Select queue state…" rather than silently pre-selecting the first
  // config key (same pattern as the gate forms below).
  const [formQueueState, setFormQueueState] = useState<QueueState | ''>(order.queueState ?? '')
  const [formWaitingOn, setFormWaitingOn] = useState<WaitingOn | ''>(order.waitingOn ?? '')
  const [formReason, setFormReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // SA-271: duplicate-resolution form (potential_duplicate missions only).
  const [dupMatchedOrderId, setDupMatchedOrderId] = useState('')
  const [dupReason, setDupReason] = useState('')
  const [dupSubmitting, setDupSubmitting] = useState(false)

  // Each fetch is caught independently so one failing endpoint (e.g. events
  // 500ing) doesn't blank the whole panel — it just renders that section's
  // empty state, matching the brief's "loading/error-tolerant" contract.
  const load = useCallback(async () => {
    setIsLoading(true)
    await Promise.all([
      chrome === 'cards' ? getOrderEvents(order.id).then(setEvents).catch(() => setEvents([])) : Promise.resolve(),
      getOrderMeasures(order.id).then(setMeasures).catch(() => setMeasures(null)),
      assigneeNames ? Promise.resolve() : getAssignees().then(setAssignees).catch(() => setAssignees([])),
      getOrderGates(order.id).then(payload => {
        setGates(payload.gates)
        setCommitment(payload.commitment)
        // Re-seed every row's form to the empty sentinel — never default to
        // the row's current state (staff clicking Update without touching
        // the dropdown must not silently resubmit that state as a manual
        // transition, e.g. overwriting an `auto_checked` rule-engine
        // verdict with an unintended `not_required`). This also means a
        // successful submit on one row discards any in-progress edits on
        // the other rows in this same reload — acceptable: edits are
        // per-row and rare enough that re-selecting is a minor cost next
        // to the correctness of never auto-submitting a real transition.
        setGateForms(Object.fromEntries(payload.gates.map(g => [g.department, EMPTY_GATE_FORM])))
      }).catch(() => { setGates([]); setCommitment(null) }),
    ])
    setIsLoading(false)
  }, [order.id, chrome, assigneeNames, getOrderEvents, getOrderMeasures, getAssignees, getOrderGates])

  useEffect(() => {
    if (order.orderType !== 'quote') return
    load()
    // load() changes identity per [order.id, get*, chrome, assigneeNames];
    // re-running on every identity change would refetch after every
    // transition/assign too — mirrors FeasibilityPanel's same guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id, order.orderType])

  // SA-287: resync the transition form when the order changes under the
  // open panel (an SSE-driven refresh, another staff member's transition) —
  // otherwise the form keeps offering the stale pre-change state.
  useEffect(() => {
    setFormQueueState(order.queueState ?? '')
    setFormWaitingOn(order.waitingOn ?? '')
  }, [order.id, order.queueState, order.waitingOn])

  if (order.orderType !== 'quote') return null

  const assigneeOptions: AssigneeOption[] = assigneeNames
    ? Object.entries(assigneeNames).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
    : assignees

  const closed = Boolean(order.outcome)
  const queueCfg = order.queueState ? QUEUE_STATE_CONFIG[order.queueState] : null

  // SA-271: similar_missions marker (raw snake_case keys — customDetails
  // passes through untransformed) + the duplicate-resolution section gate.
  const similarMissions = (order.customDetails as { similar_missions?: SimilarMissionsMarker } | null)
    ?.similar_missions
  const showDuplicateResolution = order.queueState === 'potential_duplicate' && !closed

  const handleTransition = async () => {
    if (!formQueueState || !formReason.trim()) return
    setSubmitting(true)
    setActionError(null)
    try {
      await updateQueueState(order.id, {
        queueState: formQueueState,
        waitingOn: formWaitingOn || undefined,
        reason: formReason.trim(),
      })
      setFormReason('')
      onChanged()
      await load()
    } catch (e) {
      setActionError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleResolveDuplicate = async (action: 'proceed_new' | 'reuse_delta') => {
    if (!dupReason.trim()) return
    if (action === 'reuse_delta' && !dupMatchedOrderId) return
    setDupSubmitting(true)
    setActionError(null)
    try {
      await resolveDuplicate(order.id, {
        action,
        matchedOrderId: action === 'reuse_delta' ? dupMatchedOrderId : undefined,
        reason: dupReason.trim(),
      })
      setDupReason('')
      setDupMatchedOrderId('')
      onChanged()
      await load()
    } catch (e) {
      setActionError((e as Error).message)
    } finally {
      setDupSubmitting(false)
    }
  }

  const handleAssign = async (userId: string) => {
    setAssigning(true)
    setActionError(null)
    try {
      await assignOrder(order.id, userId || null)
      onChanged()
      // Fix 6 (final-review wave): reload so the new assignment_changed
      // event appears in the timeline without a remount.
      await load()
    } catch (e) {
      setActionError((e as Error).message)
    } finally {
      setAssigning(false)
    }
  }

  const handleGateSubmit = async (department: string) => {
    const form = gateForms[department]
    // No explicit selection yet (still the placeholder sentinel) — refuse
    // rather than silently resubmit the row's current state.
    if (!form || !form.state) return
    if (form.state === 'not_acceptable' && !form.reason.trim()) return
    setGateSubmitting(department)
    setActionError(null)
    try {
      await updateGateState(order.id, department, {
        state: form.state,
        reason: form.reason.trim() || undefined,
      })
      onChanged()
      await load()
    } catch (e) {
      setActionError((e as Error).message)
    } finally {
      setGateSubmitting(null)
    }
  }

  const queueBody = (
    <>
      {queueCfg ? (
        <div className="space-y-1">
          <span
            data-testid="queue-chip"
            className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', queueCfg.className)}
          >
            {queueCfg.label}
          </span>
          {order.queueStateEnteredAt && (
            <p className="text-xs text-muted-foreground">
              Since {formatRelativeTime(new Date(order.queueStateEnteredAt).getTime())}
            </p>
          )}
          {order.waitingOn && (
            <p className="text-sm text-muted-foreground">Waiting on {WAITING_ON_LABELS[order.waitingOn]}</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No queue state set yet.</p>
      )}

      {order.reuseOfOrderId && (
        <p className="text-sm text-muted-foreground mt-1">
          Reuse of {order.reuseOfOrderNumber ?? order.reuseOfOrderId}
        </p>
      )}

      <div className="mt-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Assigned to</p>
        <select
          aria-label="Assigned to"
          value={order.assignedTo ?? ''}
          disabled={assigning}
          onChange={e => handleAssign(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">Unassigned</option>
          {assigneeOptions.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      {!closed && (
        <div className="space-y-2 border-t border-border pt-3 mt-3">
          <select
            aria-label="Queue state"
            value={formQueueState}
            onChange={e => setFormQueueState(e.target.value as QueueState)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="" disabled>Select queue state…</option>
            {QUEUE_STATE_KEYS.map(qs => (
              <option key={qs} value={qs}>{QUEUE_STATE_CONFIG[qs].label}</option>
            ))}
          </select>
          <select
            aria-label="Waiting on"
            value={formWaitingOn}
            onChange={e => setFormWaitingOn(e.target.value as WaitingOn | '')}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">Not specified</option>
            {WAITING_ON_KEYS.map(w => (
              <option key={w} value={w}>{WAITING_ON_LABELS[w]}</option>
            ))}
          </select>
          <input
            aria-label="Reason"
            type="text"
            value={formReason}
            onChange={e => setFormReason(e.target.value)}
            placeholder="Reason for this transition (required)"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            onClick={handleTransition}
            disabled={submitting || !formQueueState || !formReason.trim()}
            className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Update queue state'}
          </button>
        </div>
      )}

      {actionError && <p className="text-sm text-destructive mt-2">{actionError}</p>}
    </>
  )

  const similarBody = (
    <>
      {!similarMissions?.checked ? (
        <p className="text-sm text-muted-foreground">Similar-order check unavailable.</p>
      ) : !similarMissions.matches?.length ? (
        <p className="text-sm text-muted-foreground">No similar orders found.</p>
      ) : (
        <ul className="space-y-1 mb-2">
          {similarMissions.matches.map(m => (
            <li key={m.order_id} className="text-sm text-foreground">
              {m.order_number} — requested {m.created_at ? m.created_at.slice(0, 10) : 'unknown date'} —{' '}
              analysis: {m.decision_state ?? 'none recorded'} — outcome: {m.outcome ?? 'open'} —{' '}
              {Math.round(m.score * 100)}% ({m.matched_factors.join(', ')})
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2 border-t border-border pt-3 mt-3">
        <select
          aria-label="Reuse of"
          value={dupMatchedOrderId}
          onChange={e => setDupMatchedOrderId(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">Not a reuse — proceeding as new</option>
          {(similarMissions?.matches ?? []).map(m => (
            <option key={m.order_id} value={m.order_id}>{m.order_number}</option>
          ))}
        </select>
        <input
          aria-label="Resolution reason"
          type="text"
          value={dupReason}
          onChange={e => setDupReason(e.target.value)}
          placeholder="Resolution reason (required)"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <div className="flex flex-wrap gap-1">
          {DUPLICATE_REASON_QUICK_PICKS.map(r => (
            <button
              key={r}
              type="button"
              onClick={() => setDupReason(r)}
              className="px-2 py-1 text-xs rounded-md border border-border text-muted-foreground hover:bg-muted"
            >
              {r}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleResolveDuplicate('proceed_new')}
            disabled={dupSubmitting || !dupReason.trim()}
            className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50"
          >
            {dupSubmitting ? 'Saving…' : 'Proceed as new'}
          </button>
          <button
            onClick={() => handleResolveDuplicate('reuse_delta')}
            disabled={dupSubmitting || !dupReason.trim() || !dupMatchedOrderId}
            className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50"
          >
            {dupSubmitting ? 'Saving…' : 'Reuse prior analysis'}
          </button>
        </div>
      </div>
    </>
  )

  const gatesBody = (
    <>
      {commitment && (
        <p data-testid="gates-commitment-line" className="text-sm text-foreground mb-3">
          Worst gate:{' '}
          {commitment.worstGate ? GATE_STATE_CONFIG[commitment.worstGate].label : 'None'}
          {' '}· {commitment.unresolved.length} unresolved
        </p>
      )}

      <ul className="space-y-3">
        {gates.map(g => {
          const form = gateForms[g.department] ?? EMPTY_GATE_FORM
          const needsReason = form.state === 'not_acceptable'
          return (
            <li key={g.department} className="border-t border-border pt-3 first:border-t-0 first:pt-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-foreground">{titleizeSlug(g.department)}</span>
                <span
                  data-testid={`gate-chip-${g.department}`}
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                    GATE_STATE_CONFIG[g.state].className,
                  )}
                >
                  {GATE_STATE_CONFIG[g.state].label}
                </span>
              </div>

              {!closed && (
                <div className="space-y-2 mt-2">
                  <select
                    aria-label={`${g.department} state`}
                    value={form.state}
                    onChange={e => setGateForms(prev => ({
                      ...prev,
                      // Selecting a real state always clears any reason
                      // typed under a previous (possibly different)
                      // selection — never carry stale reason text onto
                      // a state that doesn't need it.
                      [g.department]: { state: e.target.value as GateState, reason: '' },
                    }))}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  >
                    <option value="" disabled>Select new state…</option>
                    {STAFF_SETTABLE_GATE_STATES.map(s => (
                      <option key={s} value={s}>{GATE_STATE_CONFIG[s].label}</option>
                    ))}
                  </select>
                  {needsReason && (
                    <input
                      aria-label={`${g.department} reason`}
                      type="text"
                      value={form.reason}
                      onChange={e => setGateForms(prev => ({
                        ...prev,
                        [g.department]: { ...form, reason: e.target.value },
                      }))}
                      placeholder="Reason (required)"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                  )}
                  <button
                    onClick={() => handleGateSubmit(g.department)}
                    disabled={
                      gateSubmitting === g.department || !form.state || (needsReason && !form.reason.trim())
                    }
                    className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50"
                  >
                    {gateSubmitting === g.department ? 'Saving…' : `Update ${g.department}`}
                  </button>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </>
  )

  const firstQuote = firstQuoteState(order.firstQuote, order, new Date())
  const measuresBody = measures && (
    <div className="space-y-1 text-sm text-foreground">
      <p>
        End-to-end: {measures.endToEndMinutes != null ? `${Math.round(measures.endToEndMinutes)} min` : 'In progress'}
      </p>
      {firstQuote && (
        <p>{MEASURE_LABEL} {firstQuote.kind === 'sent' ? durationWords(firstQuote.minutes) : NO_QUOTE_YET}</p>
      )}
      <p>
        Touch time: {Math.round(measures.touchTimeMinutes)} min{' '}
        <span className="text-xs text-muted-foreground">({measures.touchTimeMethod})</span>
      </p>
      <p className="text-xs text-muted-foreground">
        Wait by owner:{' '}
        {WAITING_ON_KEYS
          .map(w => `${WAITING_ON_LABELS[w]} ${Math.round(measures.waitByOwnerMinutes[w])}m`)
          .join(' · ')}
      </p>
      {measures.revisionResponseMinutes.median != null && (
        <p className="text-xs text-muted-foreground">
          Revision response (median): {Math.round(measures.revisionResponseMinutes.median)} min
          {' '}({measures.revisionResponseMinutes.samples.length} samples)
        </p>
      )}
      {measures.decisionLatencyMinutes.openSince && (
        <p className="text-xs text-muted-foreground">
          Decision pending since {new Date(measures.decisionLatencyMinutes.openSince).toLocaleString()}
        </p>
      )}
    </div>
  )

  const timelineBody = (
    <>
      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground">No events yet.</p>
      ) : (
        <ul className="space-y-1">
          {events.map(e => {
            // E3 (spec §5.4): the two events that carry an AI's hand say
            // what happened; everything else keeps its label alone.
            const ai = describeProcessEvent(e.eventType, e.payload)
            return (
              <li key={e.id} className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{ai?.label ?? humanizeEventType(e.eventType, noun)}</span>
                <span className="text-xs"> · {new Date(e.occurredAt).toLocaleString()}</span>
                {ai?.detail && <p className="text-xs">{ai.detail}</p>}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )

  if (chrome === 'section') {
    return (
      <RecordSection id="queue" title="Queue" summary={queueSummary(order, assigneeNames)} defaultOpen={false} focused={focused}>
        <div className="space-y-3">
          <div>{queueBody}</div>
          {showDuplicateResolution && <Block title="Similar orders">{similarBody}</Block>}
          {gates.length > 0 && <Block title="Gates">{gatesBody}</Block>}
          {!isLoading && measures && <Block title="Measures">{measuresBody}</Block>}
        </div>
      </RecordSection>
    )
  }

  return (
    <>
      <div className="bg-card rounded-lg p-4 border border-border">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Queue</h3>
        {queueBody}
      </div>

      {showDuplicateResolution && (
        <div className="bg-card rounded-lg p-4 border border-border">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Similar orders</h3>
          {similarBody}
        </div>
      )}

      {gates.length > 0 && (
        <div className="bg-card rounded-lg p-4 border border-border">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Gates</h3>
          {gatesBody}
        </div>
      )}

      {!isLoading && measures && (
        <div className="bg-card rounded-lg p-4 border border-border">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Measures</h3>
          {measuresBody}
        </div>
      )}

      {!isLoading && (
        <div className="bg-card rounded-lg p-4 border border-border">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Timeline</h3>
          {timelineBody}
        </div>
      )}
    </>
  )
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-t border-border pt-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      {children}
    </div>
  )
}
